/**
 * The one place engagement WebSocket names and resource strings exist.
 *
 * CV-5 (ENG-5498) landed: engagement live updates ride the generic
 * `conversation` WS service — the api no longer registers the legacy
 * `engagement` service name, and the owning domain moved into the RESOURCE.
 * This module stays the single indirection so any future naming change is
 * again a one-file cutover — do not inline queue ids or resource strings at
 * call sites.
 *
 * The queue-id literals live in `core/apiWebSocket` because the transport's
 * `QueueId` is a closed union; re-exporting them here keeps a single source of
 * truth rather than a second copy that could drift.
 *
 * GraphQL messaging wiring is CV-safe by contract (ENG-5417 freeze): operation
 * names, `engagement.*` error codes and payload shapes are frozen at the edge.
 * Only the WS naming moved. (Event payloads now key contextType/contextId
 * instead of engagementId — no consumer reads them: by D7 a payload is only a
 * signal to re-read.)
 */
import type { EngagementChannelType } from '@/__generated__/graphql';
import { QUEUE_IDS } from '@/core/apiWebSocket';

export const ENGAGEMENT_QUEUE_IDS = {
  NEW_MESSAGE: QUEUE_IDS.CONVERSATION_NEW_MESSAGE,
  UNREAD_MESSAGES: QUEUE_IDS.CONVERSATION_UNREAD_MESSAGES,
} as const;

/**
 * Resource key for a single engagement channel, per the api's strict
 * three-segment contract (`conversation/service/websocket.go`
 * parseChannelResource): `engagement:<engagementID>:<channelType>`. The old
 * two-segment `<engagementID>:<channelType>` form is refused by the
 * authorizer (fail-closed), so the `engagement:` prefix is load-bearing.
 */
export function engagementChannelResource(
  engagementId: string,
  channelType: EngagementChannelType
): string {
  return `engagement:${engagementId}:${channelType}`;
}
